import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Shield } from 'lucide-react';
import { Header } from '../components/Header';
import { Seo } from '../components/Seo';

const faqs = [
  {
    question: "Why do I have a Futureworkhelp charge on my bank statement?",
    answer: "If a direct debit appears on your statement, you have probably signed up to a subscription to one of our partner sites for which we provide the after-sales service. Please note that you are not a client of Futureworkhelp directly, but a client of a site that has delegated the management of its customer service."
  },
  {
    question: "What are the services offered by Futureworkhelp?",
    answer: "Futureworkhelp offers simplified management of your subscription. Fill in the form on the home page with an e-mail address used at the time of subscription. You will be able to contact the agents responsible for your file, obtain all the information concerning your subscription or cancel it."
  },
  {
    question: "What kind of subscription do you need for after-sales service?",
    answer: "Futureworkhelp provides customer service subscriptions that make your life easier. We work with sites that offer help to individuals or professionals, accompanying the client during various operations or steps."
  },
  {
    question: "I do not remember signing up to a Futureworkhelp subscription.",
    answer: "You have not signed up to a Futureworkhelp subscription. You have definitely signed up to a subscription at one of our partner sites for which we provide after-sales service."
  },
  {
    question: "How to find the offer to which I subscribed?",
    answer: "Fill in the form on the home page with the email address used when registering. We will provide you with important information about your subscription, such as the date and amount of the next withdrawal and the service features you have selected."
  },
  {
    question: "I forgot the email address used when subscribing. What can I do?",
    answer: "If you are unable to retrieve the email address used during the subscription, our agents always have the possibility to find your client file. Contact our agents by email or chat."
  },
  {
    question: "Who can I contact if I have a question?",
    answer: "Our agents are experts in after-sales service and are available 24/7. Contact them by live chat or by phone via our toll-free number any time you need. You can also send them a message."
  },
  {
    question: "How can I cancel my subscription?",
    answer: "Go to your customer account and click the Cancel button. You will no longer benefit from the services of our partner site and will no longer be charged. A confirmation email will be sent."
  }
];

export const Faqs = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <div className="font-sans bg-[#Fcfcfc] text-gray-900 min-h-screen selection:bg-red-100 selection:text-red-900 flex flex-col">
      <Seo
        title="Frequently Asked Questions | Futureworkhelp"
        description="Find answers to common questions about subscriptions, billing, charges, cancellations, and customer support at Futureworkhelp."
        socialDescription="Find answers to frequently asked questions about subscriptions, billing, cancellation, and customer support at Futureworkhelp."
        url="https://futureworkhelp.net/faq/"
        imageAlt="Frequently asked questions about subscriptions and customer support"
      />
      
      <Header />

      {/* Main Content */}
      <main className="flex-grow pt-32 pb-16 lg:pt-40 lg:pb-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        
        {/* Abstract Background Elements */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[30rem] bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-red-50/80 via-white to-transparent opacity-80 pointer-events-none"></div>

        <div className="max-w-3xl mx-auto relative z-10">
          <div className="text-center mb-10 lg:mb-16 relative">
         
            <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 tracking-tight mb-4">
              Knowledge <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-red-400">Base</span>
            </h1>
            <p className="text-lg text-gray-500 font-medium max-w-xl mx-auto">
              Find immediate answers to your billing, subscription, and account management queries securely.
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index} 
                className={`bg-white rounded-[1.5rem] border transition-all duration-300 overflow-hidden ${openIndex === index ? 'border-red-200 shadow-[0_10px_40px_rgba(220,38,38,0.08)]' : 'border-gray-100 shadow-sm hover:border-gray-200 hover:shadow-md'}`}
              >
                <h2 className="m-0">
                  <button
                    className="w-full px-6 sm:px-8 py-5 sm:py-6 text-left flex items-center justify-between focus:outline-none group"
                    onClick={() => setOpenIndex(openIndex === index ? null : index)}
                    aria-expanded={openIndex === index}
                  >
                    <span className={`text-lg font-bold pr-8 transition-colors ${openIndex === index ? 'text-gray-900' : 'text-gray-700 group-hover:text-red-600'}`}>{faq.question}</span>
                    <span className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-transform duration-300 border ${openIndex === index ? 'rotate-180 bg-red-50 text-red-600 border-red-100' : 'bg-gray-50 text-gray-400 border-gray-100 group-hover:bg-gray-100'}`}>
                      <ChevronDown className="w-5 h-5" />
                    </span>
                  </button>
                </h2>
                
                <div 
                  className={`px-6 sm:px-8 transition-all duration-500 ease-in-out overflow-hidden ${openIndex === index ? 'max-h-[500px] opacity-100 pb-6 sm:pb-8' : 'max-h-0 opacity-0'}`}
                >
                  <p className="text-gray-500 leading-relaxed font-medium">
                    {faq.answer}
                  </p>
                </div>
              </div>
            ))}
          </div>

        </div>
      </main>

      {/* Professional Footer */}
      <footer className="bg-white border-t border-gray-200 py-12 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="notranslate flex items-center gap-2" translate="no">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-red-600">
                <Shield className="h-5 w-5 text-white" />
              </div>
              <span className="text-xl font-bold tracking-tight text-gray-900">futureworkhelp.net</span>
            </div>
            <div className="flex gap-6 text-sm font-medium text-gray-500">
              <Link to="/" className="hover:text-red-600 transition-colors">Home</Link>
              <Link to="/faq/" className="hover:text-red-600 transition-colors">FAQ</Link>
            </div>
            <p className="text-sm text-gray-400 font-medium">
              &copy; {new Date().getFullYear()} <span className="notranslate" translate="no">futureworkhelp.net</span>. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
      
    </div>
  );
};
